/**
 * Variant definitions for the jaundice calculator.
 *
 * The whole implementation lives in `PhototherapyApp.tsx` and the two calculators
 * beside it; every registered app is a thin wrapper that binds one of these
 * variants. A variant is what makes two apps that share code point at *different*
 * AppConfig rows (`appKey`), so each hospital configures them separately.
 *
 * The two registered apps differ in exactly three things — the name, the route,
 * and which calculator a hospital that has never been configured gets:
 *
 *   | | צהבת ביילוד | צהבת ביילוד AAP 2022 |
 *   |---|---|---|
 *   | route | /main/<hospital>/phototherapy | /main/<hospital>/bili2022 |
 *   | AppConfig appName | PHOTOTHERAPY | BILI2022 |
 *   | default protocol | israeliCurves | aap2022 |
 *
 * Both apps still offer both calculators: a hospital on `bili2022` may switch to
 * the Israeli curves on the admin page, and one on `phototherapy` may switch to
 * AAP 2022. `defaultProtocol` only decides what happens when there is no stored
 * row at all.
 */
import type { PhototherapyProtocol } from './phototherapyConfig';

export interface PhototherapyVariant {
  /** AppConfig `appName` — also the `App.key` the admin permission check uses. */
  appKey: string;
  /** AppConfig `configName`. */
  configName: string;
  /** Route slug: `/main/<hospital>/<appToken>`. */
  appToken: string;
  /**
   * The app's name. Feeds the breadcrumb (`data-page-title`) and the admin page
   * heading — NOT the in-app header, which names the calculator on screen
   * ("צהבת ביילוד" + an "AAP 2022" subsection) and is the same in both apps.
   */
  title: string;
  /** The calculator a hospital with no stored AppConfig row gets. */
  defaultProtocol: PhototherapyProtocol;
}

export const PHOTOTHERAPY_VARIANT: PhototherapyVariant = {
  appKey: 'PHOTOTHERAPY',
  configName: 'settings',
  appToken: 'phototherapy',
  title: 'צהבת ביילוד',
  defaultProtocol: 'israeliCurves',
};

export const BILI2022_VARIANT: PhototherapyVariant = {
  appKey: 'BILI2022',
  configName: 'settings',
  appToken: 'bili2022',
  title: 'צהבת ביילוד AAP 2022',
  defaultProtocol: 'aap2022',
};
